// @LTG-ID: data/journals/sde-vs-ebitda-guide
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 4 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "sde-vs-ebitda-guide",
    FRONTMATTER: {
        number: "41",
        section: "Valuation",
        sectionHref: "/journals#core-valuation",
        sectionAnchor: "core-valuation",
        title: "SDE vs EBITDA: Which One Applies to Your Business?",
        deck: "SDE, or seller's discretionary earnings, measures the total financial benefit available to one full-time owner-operator, including the owner's salary and perks.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-20",
        publishedHuman: "May 20, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 436,
        timeRequired: "PT3M",
        keywords: "SDE vs EBITDA, seller's discretionary earnings, SDE meaning, difference between SDE and EBITDA",
        articleSection: "Valuation",
        articleBodyPreview: "SDE, or seller's discretionary earnings, measures the total financial benefit available to one full time owner operator, including the owner's salary and perks. EBITDA measures operating profit after a market rate manager's salary. Small owner run businesses are valued on SDE; larger, management run businesses are valued on EBITDA. Two owners can look at the same profit and loss statement and calculate two very different earnings numbers, both correct. The difference is who the earnings are for.",
        complianceNote: "This article is educational and does not constitute financial, legal, or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-much-is-my-business-worth",
        "what-is-ebitda",
        "what-is-a-valuation-multiple"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "SDE vs EBITDA: Which One Applies to Your Business?",
        canonical: "https://letstalkgrowth.com/journals/sde-vs-ebitda-guide",
        ogImage: "/journals/og/sde-vs-ebitda-guide.jpg",
    },
    BODY_MARKDOWN: `SDE, or seller's discretionary earnings, measures the total financial benefit available to one full-time owner-operator, including the owner's salary and perks. EBITDA measures operating profit after a market-rate manager's salary. Small owner-run businesses are valued on SDE; larger, management-run businesses are valued on EBITDA.

Two owners can look at the same profit and loss statement and calculate two very different earnings numbers, both correct. The difference is who the earnings are for.

## SDE: the owner-operator's number

Seller's discretionary earnings answers this question: if one person bought this business and worked in it full time, what total financial benefit would they receive? SDE starts with pre-tax profit and adds back interest, depreciation, amortization, one-time items, and, critically, the owner's entire compensation package: salary, payroll taxes, health insurance, vehicle, and legitimate perks.

SDE is the standard earnings measure for Main Street businesses, the restaurants, service businesses, small retailers, and trades companies where the buyer will typically step into the owner's job.

## EBITDA: the investor's number

EBITDA answers a different question: what does this business earn as a standalone operation with a paid manager running it? EBITDA therefore includes a deduction for a market-rate salary for whoever manages the company. A buyer who will not work in the business, such as a private equity group or a strategic acquirer, thinks in EBITDA, because they will have to pay someone to do what the owner does.

## The bridge between them

The relationship is straightforward: SDE minus a market-rate manager's salary approximately equals adjusted EBITDA. That single subtraction is why the same business can be described with two very different earnings figures, and why multiples for the two measures are not interchangeable.

## Why using the wrong one is expensive

Market data published in BizBuySell Insight Reports and the IBBA Market Pulse survey consistently shows that SDE multiples for small businesses and EBITDA multiples for larger businesses occupy different ranges. Owners sometimes hear an EBITDA multiple quoted for larger deals in their industry and apply it to their SDE. The result is a fantasy price that no buyer will pay, and overpriced listings are a leading reason businesses sit unsold. The reverse error, applying a small-business SDE multiple to a genuine EBITDA figure, undervalues the company.

## The rule of thumb for which to use

If the buyer of your business will most likely work in it, think SDE. If the buyer will most likely hire or retain management to run it, think EBITDA. Businesses in the transition zone between the two are often presented both ways, with the bridge shown explicitly.`,
    FAQ: [
        { question: "Which is bigger, SDE or EBITDA?", answer: "SDE is almost always larger, because it includes the owner's full compensation, while EBITDA is calculated after a market-rate management salary." },
        { question: "At what size does a business switch from SDE to EBITDA?", answer: "There is no official line, but as businesses grow beyond a single owner-operator and develop real management teams, buyers increasingly evaluate them on EBITDA. Many advisors present mid-sized businesses both ways." },
        { question: "Can I add back my spouse's salary to SDE?", answer: "Only for the portion not replaced by a real cost. If your spouse performs work the buyer will need to pay someone for, that cost stays in the numbers. Diligence teams check this closely." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Valuation",
            title: "What Is EBITDA? A Plain-English Guide for Business Owners",
            meta: "3 min · Brief 40",
            to: "/journals/what-is-ebitda",
        },
        {
            tag: "Valuation",
            title: "What Is a Valuation Multiple and Why Does Everyone Talk About Them?",
            meta: "3 min · Brief 42",
            to: "/journals/what-is-a-valuation-multiple",
        },
    ],
};

export default BRIEF;
